import type { BaseCondition, DebugStateHandler, OfferSystemConfig } from '../offerSystemTypes';
import { OfferEditorOfferMenu } from './OfferEditorOfferMenu';
import { EditorOfferView } from './EditorOfferView';
import { OfferSystemController } from '../OfferSystemController';
import { OfferSystemEditorMenu } from './OfferSystemEditorMenu';
import { OfferEditorStateMenu } from './OfferEditorStateMenu';
import { OfferEditorStateInfo } from './OfferEditorStateInfo';

export class OfferEditor<S, C extends BaseCondition, GD, SH extends DebugStateHandler<S>> {
  private readonly inactiveColumn = document.createElement('div');

  private readonly activeColumn = document.createElement('div');

  private offerViews: Record<string, EditorOfferView<S, C, GD>> = {};

  private readonly offerEditorMenu: OfferEditorOfferMenu<S, C, GD>;

  private readonly offerSystem: OfferSystemController<S, C, GD>;

  private readonly offerSystemMenu: OfferSystemEditorMenu<S, C, GD>;

  constructor(
    private readonly config: OfferSystemConfig<S, C, GD>,
    debugStateHandler: SH,
  ) {
    this.createRootView();

    this.offerEditorMenu = new OfferEditorOfferMenu(this.config);

    this.offerSystem = new OfferSystemController(this.config);

    void new OfferEditorStateInfo(this.config.state, debugStateHandler);

    this.offerSystemMenu = this.createMenu();

    void new OfferEditorStateMenu(config.state, debugStateHandler.actions);

    config.offers.forEach((offer) => {
      const offerView = new EditorOfferView({
        offer,
        systemConfig: config,
      });
      this.offerViews[offer.id] = offerView;

      offerView.element.addEventListener('click', () => {
        this.offerEditorMenu.editOffer(offer);
      });

      this.inactiveColumn.appendChild(offerView.element);
    });
  }

  private createMenu(): OfferSystemEditorMenu<S, C, GD> {
    const menu = new OfferSystemEditorMenu(this.config);
    menu.onRefreshOffer.connect((location, trigger) => {
      this.updateOffers(location, trigger, false);
    });
    menu.onSetVisibilityOfAlwaysActive.connect((visible) => {
      this.config.offers.forEach((offer) => {
        if (!offer.startConditions && !offer.endConditions) {
          const view = this.offerViews[offer.id];
          if (!view) return;
          view.setVisibility(visible);
        }
      });
    });
    menu.onPurchaseMade.connect((offerId) => {
      this.offerSystem.markPurchase(offerId);
      this.updateOffers('*', null, true);
    });
    return menu;
  }

  private updateOffers(location: string, trigger: string | null, skipUpdate: boolean): void {
    const activeOffers = this.offerSystem.getActiveOffers(location, trigger, skipUpdate);

    console.log('updateOffers', { activeOffers, skipUpdate });

    Object.entries(this.offerSystem.debugLastUpdateReport).forEach(([offerId, report]) => {
      const view = this.offerViews[offerId];
      if (!view) return;
      view.setContextData({
        report,
        remainingTime: null,
        activeVariant: null,
      });
    });

    Object.values(this.offerViews).forEach((offerView) => {
      this.inactiveColumn.appendChild(offerView.element);
    });

    activeOffers.forEach(({ offer, endDate, variant }) => {
      this.activeColumn.appendChild(this.offerViews[offer.id].element);

      this.offerViews[offer.id].setContextData({
        report: this.offerSystem.debugLastUpdateReport[offer.id] ?? null,
        remainingTime: endDate ? endDate - this.config.getCurrentTime() : null,
        activeVariant: variant,
      });
    });

    Object.values(this.offerViews).forEach((view) => {
      view.refresh();
    });

    this.offerSystemMenu.updatePurchaseOffers(activeOffers.map(({ offer }) => offer.id));
  }

  private createRootView(): HTMLDivElement {
    const root = document.createElement('div');
    Object.assign(root.style, {
      position: 'fixed',
      top: 0,
      bottom: 0,
      paddingTop: '30em',
      width: '100%',
      backgroundColor: '#D0D0D0',
      overflowY: 'scroll',
      display: 'flex',
    });
    document.body.appendChild(root);

    Object.assign(this.inactiveColumn.style, {
      height: 'auto',
      backgroundColor: '#FFB3BA',
      borderRadius: '1em',
      alignSelf: 'flex-start',
    });
    Object.assign(this.activeColumn.style, {
      height: 'auto',
      backgroundColor: '#BAFFC9',
      borderRadius: '1em',
      alignSelf: 'flex-start',
    });

    root.appendChild(this.inactiveColumn);
    root.appendChild(this.activeColumn);

    return root;
  }
}
